import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LightboxModal from '../components/LightboxModal';

export default function TreatmentPage() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/galleries?slug.da=klinikken')
      .then((res) => res.json())
      .then((data) => {
        if (data?.items?.[0]?.images) {
          setGalleryImages(data.items[0].images);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <title>Behandlingen | rolskov-bowtech.dk</title>
      <meta
        name="description"
        content="Hvordan foregår en Bowtech-behandling hos Jørn Rolskov? Behandlingen varer ca. 1 time og består af blide målrettede greb og korte pauser."
      />
      <link rel="canonical" href="https://rolskov-bowtech.dk/Behandlingen" />

      {/* Header Banner */}
      <section className="bg-[#f4efe6]/60 py-12 lg:py-16 border-b border-[#228A47]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs font-semibold text-[#228A47] uppercase tracking-wider bg-[#e8f5ed] px-3 py-1 rounded-full">
            Praktisk &amp; forløb
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#062364]">
            Behandlingen
          </h1>
          <p className="text-base sm:text-lg text-[#132038] max-w-3xl leading-relaxed">
            Hvordan foregår en Bowtech-behandling i klinikken i Skårup?
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Main Scraped Narrative */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-6 bg-white p-8 lg:p-10 rounded-3xl border border-[#228A47]/15 shadow-xs">
              <h2 className="text-2xl font-bold text-[#062364]">
                Hvordan foregår en behandling
              </h2>

              <div className="text-sm sm:text-base text-[#132038] leading-relaxed space-y-4 font-normal">
                <p>
                  Behandlingen varer ca. 1 time og består af en eller flere procedurer med få eller flere greb.
                </p>
                <p>
                  Grebene er blide men målrettede og kan, hvis det foretrækkes, udføres gennem let påklædning. Mellem hver serie af greb holder behandleren en pause (2 min.) for at kroppen kan få lov at reagere.
                </p>
                <p>
                  Nogle skader/spændinger kan afhjælpes i løbet af 1 - 2 behandlinger. Gamle og kroniske skader har ofte behov for flere behandlinger.
                </p>
                <div className="p-4 rounded-2xl bg-[#e8f5ed] border border-[#228A47]/20 text-[#062364] font-medium text-xs sm:text-sm flex items-start gap-3">
                  <i className="fa-solid fa-triangle-exclamation text-[#228A47] text-base mt-0.5 shrink-0" aria-hidden="true"></i>
                  <span>
                    Andre former for manipulerende behandling udført op til 4 dage før og 5 dage efter en Bowtechbehandling forstyrrer dens effektivitet.
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-md border border-[#228A47]/15 bg-white h-full">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/0424b756-a2fd-4ec3-9a16-f52eef89d933/8390.jpg"
                  alt="Klient under Bowtech behandling"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Key Elements Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg">
                <i className="fa-regular fa-clock" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-[#062364]">Varighed: ca. 1 time</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Der sættes god tid af til den enkelte klient i rolige og trygge rammer.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg">
                <i className="fa-regular fa-shirt" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-[#062364]">Uden på tøjet</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Grebene udføres blidt og kan hvis ønsket udføres igennem let og behagelig påklædning.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-[#228A47]/10 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#e8f5ed] text-[#228A47] flex items-center justify-center text-lg">
                <i className="fa-regular fa-pause" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-[#062364]">2-minutters pauser</h3>
              <p className="text-xs text-[#132038] leading-relaxed">
                Pauserne mellem grebserierne giver nervesystemet ro til at integrere stimulansen.
              </p>
            </div>
          </div>

          {/* Gallery Section */}
          {galleryImages.length > 0 && (
            <div className="space-y-6 pt-4">
              <div className="border-b border-[#228A47]/10 pb-4">
                <h3 className="text-xl font-bold text-[#062364]">
                  Galleribilleder fra klinikken &amp; behandlingsrummet
                </h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveLightboxIndex(idx)}
                    className="group relative rounded-xl overflow-hidden aspect-4/3 border border-[#228A47]/10 bg-white focus:outline-none focus:ring-2 focus:ring-[#228A47]"
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                      alt={`Klinikbillede ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lightbox Modal */}
          <LightboxModal
            images={galleryImages}
            activeIndex={activeLightboxIndex}
            onClose={() => setActiveLightboxIndex(null)}
            onSelectIndex={(i) => setActiveLightboxIndex(i)}
          />

          {/* CTA Box */}
          <div className="bg-[#062364] text-white p-8 sm:p-10 rounded-3xl text-center space-y-4">
            <h3 className="text-2xl font-bold">Klar til at bestille en tid?</h3>
            <p className="text-sm text-slate-200 max-w-xl mx-auto">
              Behandling foregår efter aftale i klinikken på Østergade 47 i Skårup på Fyn. Pris: 400 kr.
            </p>
            <div className="pt-2 flex flex-wrap justify-center gap-4">
              <a
                href="tel:27117557"
                className="whitespace-nowrap px-6 py-3 text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] rounded-xl transition-colors"
              >
                Ring på 27 11 75 57
              </a>
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                Send en besked
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
